import type { IMedia, Question, ValidationCallback } from 'shared-domains';
import type { MetaQuestionResponse } from '~/utils/ComprehensionQuestionUtils';
import type Constants from '~/config/Constants';

export interface ITiptapValue {
  text: string,
  isEmpty: boolean,
  isFocused?: boolean,
}

export interface OptionUpdate {
  type?: 'text' | 'image',
  text?: string,
  hasMath?: boolean,
  math?: {
    latex: string[],
    template: string | null,
  },
  media?: IMedia[],
}

export type IValidationCallback = ValidationCallback;

export type IPreSaveCallback = (question: Question) => Question;

export type BrandColors = 'bg-brand-a' | 'bg-brand-b' | 'bg-brand-c' | 'bg-brand-d' | 'bg-brand-e';

export type BrandColorsDark = 'bg-brand-a-dark' | 'bg-brand-b-dark' | 'bg-brand-c-dark' | 'bg-brand-d-dark' | 'bg-brand-e-dark';

export type FontColors = '#E0525E' | '#CC7700' | '#1F9350' | '#2D9DA6' | '#5C9AD6' | '#FFFFFF' | '#9A4292' | '#D5546D';

export interface Symbol {
  code: string,
  name: string,
}

export type OptionActions = 'delete' | 'convert-to-text' | 'image';

export enum QuizSettingsModalModes {
  PUBLISH = 'publish',
  SETTINGS = 'settings',
  EDIT_NAME = 'edit-name',
  EDIT_GRADE = 'edit-grade',
  EDIT_SUBJECT = 'edit-subject',
  EDIT_IMAGE = 'edit-image',
  EDIT_LANGUAGE = 'edit-language',
  EDIT_VISIBILITY = 'edit-visibility',

  // Lesson
  EDIT_LESSON_SETTINGS = 'edit-lesson-settings',
}

export enum OptionShortcutKeys {
  DELETE = 'd',
  IMAGE = 'g',
  MATH = 'e',
  ANSWER = 'm',
}

export interface Color {
  name: string,
  value: FontColors,
}

type BulkTimeActionModes = 'BULK_TIME_UPDATE_LOADING' | 'BULK_TIME_UPDATE_SUCCESS' | 'BULK_TIME_UPDATE_FAILED';
type BulkPointsActionModes = 'BULK_POINT_UPDATE_LOADING' | 'BULK_POINT_UPDATE_SUCCESS' | 'BULK_POINT_UPDATE_FAILED';
type BulkDeletionModes = 'BULK_DELETION_LOADING' | 'BULK_DELETION_SUCCESS' | 'BULK_DELETION_FAILED';
type QuestionActions = 'DELETING_QUESTION' | 'DELETING_QUESTION_SUCCESS' | 'DELETING_QUESTION_FAILED' | 'DUPLICATING_QUESTION' | 'DUPLICATING_QUESTION_SUCCESS' | 'DUPLICATING_QUESTION_FAILED';
type QuestionConversionModes = 'CONVERTING_QUESTION' | 'CONVERT_QUESTION_SUCCESS' | 'CONVERT_QUESTION_FAILED';
type PublishingModes = 'PUBLISHING_QUIZ' | 'PUBLISH_QUIZ_SUCCESS' | 'PUBLISH_QUIZ_FAILED';
type QuestionSavingModes = 'QUESTION_CREATING' | 'QUESTION_UPDATING' | 'QUESTION_CREATED' | 'QUESTION_UPDATED' | 'QUESTION_CREATE_FAILED' | 'QUESTION_UPDATE_FAILED';
type MediaPastingModes = 'MEDIA_PASTING' | 'MEDIA_PASTED' | 'MEDIA_PASTE_FAILED';

export const QuestionEditorExitGuardSkipArray = ['QUESTION_CREATED', 'QUESTION_UPDATED', 'QUESTION_CREATE_FAILED', 'QUESTION_UPDATE_FAILED', 'VERSION_MISMATCH', 'PUBLISH_QUIZ_SUCCESS'];
const EditorErrorArray = ['QUIZ_NOT_FOUND', 'NO_ACCESS', 'QUIZ_LOCKED', 'VERSION_MISMATCH'];
export type EditorErrors = typeof EditorErrorArray[number];

export function IsEditorInErrorMode(mode: EditorModes): boolean {
  return EditorErrorArray.includes(mode);
}

export type EditorModes = 'IDLE'
| 'QUIZ_LOADING'
| 'QUIZ_LOADED'
| 'QUESTION_LOADING'
| 'QUESTION_LOADED'
| 'TELEPORTING_QUIZ'
| 'TELEPORTING_QUESTION'
| 'TELEPORT_QUIZ_SUCCESS'
| 'TELEPORT_QUESTION_SUCCESS'
| 'TELEPORT_QUIZ_FAILED'
| 'TELEPORT_QUESTION_FAILED'
| BulkTimeActionModes
| BulkPointsActionModes
| BulkDeletionModes
| EditorErrors
| QuestionConversionModes
| PublishingModes
| QuestionSavingModes
| QuestionActions
| MediaPastingModes;

export interface IStandard {
  _id: string,
  chapter: string,
  curriculum: string,
  description: string,
  grade: string,
  short_code: string,
  subject: string,
  title: string,
}

export interface IStandardTag {
  id: string,
  code: string,
  title: string,
}

export interface ICurriculum {
  all_grades?: string[],
  all_subjects?: string[],
  applicable_grades?: string[],
  applicable_subjects?: string[],
  country: string[],
  description: string,
  global: boolean,
  name: string,
  state: string[],
  _id: string,
  _v?: string,
}

interface QuestionFilterCallback {
  (question: Question): boolean,
}

interface QuestionSaveCallback {
  (responseQuestion: MetaQuestionResponse): Promise<void>,
}

interface QuestionCancelCallback {
  (question?: Question): void,
}

interface QuestionConvertCallback {
  (question?: Question): Question,
}

interface QuestionExitCallback {
  (question?: Question): void,
}

export interface QuestionCallbacks {
  filter?: null | QuestionFilterCallback,
  save?: null | QuestionSaveCallback,
  cancel?: null | QuestionCancelCallback,
  convert?: null | QuestionConvertCallback,
  exit?: null | QuestionExitCallback,
}

export enum RichTextSupportedMediaNodes {
  IMG = 'img',
  AUDIO = 'audio',
  VIDEO = 'video',
}
export interface RichTextMediaEvent {
  mediaNode: RichTextSupportedMediaNodes,
  src: string,
}

export interface EditorButtonsVisibility {
  showFontToolbar: boolean,
  showInsertEquation: boolean,
  showChangePoints: boolean,
  showAddTags: boolean,
  showAddAnswerExplanation: boolean,
  showParticipantsView: boolean,
  shouldAutoFocus?: boolean,
  showTimestamp?: boolean,
}

export interface IQuizSettings {
  grade: string[],
  image: string,
  lang: string,
  name: string,
  subjects: string[],
  visibility: keyof typeof Constants.ShareQuizAdvPermissionTypes,
}
